import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiBookOpen, 
  FiFileText, 
  FiAward, 
  FiSettings,
  FiArrowRight,
  FiDollarSign,
  FiTag,
  FiPackage,
  FiShoppingBag,
} from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const AdminDashboardContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 2.5vw + 1rem, 2.5rem);
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: clamp(1rem, 0.5vw + 0.875rem, 1.125rem);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatCard = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  
  &.users {
    background-color: var(--info-color);
  }
  
  &.courses {
    background-color: var(--success-color);
  }
  
  &.assignments {
    background-color: var(--warning-color);
  }
  
  &.grades {
    background-color: var(--accent-color);
  }
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: clamp(1.5rem, 2vw + 1rem, 2rem);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;



const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const QuickActionCard = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 44px;
  touch-action: manipulation;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const QuickActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: var(--text-inverse);
`;

const QuickActionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`;

const QuickActionDescription = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const QuickActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  min-height: 44px;
  padding: 0.5rem 0;
  touch-action: manipulation;
  
  &:hover {
    color: var(--primary-dark);
    transform: translateX(4px);
  }
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const RecentActivitySection = styled.div`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.25rem, 2vw + 1rem, 1.5rem);
  font-weight: 500;
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: var(--background-secondary);
  border-radius: 0;
`;

const ActivityIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  font-size: 0.75rem;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
`;

const ActivityTime = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;


function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalAssignments: 0,
    averageGrade: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Ensure page starts at top on mobile when component mounts
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Get users count
      const usersResponse = await apiService.getUsers();
      const totalUsers = usersResponse.success ? usersResponse.users.length : 0;
      
      // Get courses count
      const coursesResponse = await apiService.getCourses();
      const totalCourses = coursesResponse.success ? coursesResponse.courses.length : 0;
      
      // For now, set assignments and grades to 0 (will be implemented when assignments are added)
      setStats({
        totalUsers,
        totalCourses,
        totalAssignments: 0,
        averageGrade: 0
      });

      setRecentActivity([]);
    } catch (error) {
      logger.error('Error fetching dashboard data', { error });
      setStats({
        totalUsers: 0,
        totalCourses: 0,
        totalAssignments: 0,
        averageGrade: 0
      });
      setRecentActivity([]);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user':
        return <FiUsers size={16} />;
      case 'course':
        return <FiBookOpen size={16} />;
      case 'assignment':
        return <FiFileText size={16} />;
      default:
        return <FiSettings size={16} />;
    }
  };

  return (
    <AdminDashboardContainer>
      <Header>
        <Title>Admin Dashboard</Title>
        <Subtitle>Manage your Faith Insight Portal</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatHeader>
            <StatIcon className="users">
              <FiUsers size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{stats.totalUsers}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatInfo>
          </StatHeader>

        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatHeader>
            <StatIcon className="courses">
              <FiBookOpen size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{stats.totalCourses}</StatValue>
              <StatLabel>Active Courses</StatLabel>
            </StatInfo>
          </StatHeader>

        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <StatHeader>
            <StatIcon className="assignments">
              <FiFileText size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{stats.totalAssignments}</StatValue>
              <StatLabel>Total Assignments</StatLabel>
            </StatInfo>
          </StatHeader>

        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <StatHeader>
            <StatIcon className="grades">
              <FiAward size={24} />
            </StatIcon>
            <StatInfo>
              <StatValue>{stats.averageGrade}%</StatValue>
              <StatLabel>Average Grade</StatLabel>
            </StatInfo>
          </StatHeader>

        </StatCard>
      </StatsGrid>

      <QuickActionsGrid>
        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiUsers size={20} />
            </QuickActionIcon>
            <QuickActionTitle>User Management</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Add, edit, and manage users including students, instructors, and administrators.
          </QuickActionDescription>
          <QuickActionButton to="/admin/users">
            Manage Users
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>

        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiBookOpen size={20} />
            </QuickActionIcon>
            <QuickActionTitle>Course Management</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Create, edit, and manage courses, assignments, and course materials.
          </QuickActionDescription>
          <QuickActionButton to="/admin/courses">
            Manage Courses
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>

        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiDollarSign size={20} />
            </QuickActionIcon>
            <QuickActionTitle>Course Pricing</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Set pricing for courses and manage payment options for student enrollment.
          </QuickActionDescription>
          <QuickActionButton to="/admin/pricing">
            Manage Pricing
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>

        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.8 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiTag size={20} />
            </QuickActionIcon>
            <QuickActionTitle>Promo Codes</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Create and manage discount codes for special promotions and student incentives.
          </QuickActionDescription>
          <QuickActionButton to="/admin/promo-codes">
            Manage Promo Codes
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>

        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiPackage size={20} />
            </QuickActionIcon>
            <QuickActionTitle>Bulk Pricing</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Set up special pricing rules for students purchasing multiple courses together.
          </QuickActionDescription>
          <QuickActionButton to="/admin/bulk-pricing">
            Manage Bulk Pricing
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>

        <QuickActionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 1.0 }}
        >
          <QuickActionHeader>
            <QuickActionIcon>
              <FiShoppingBag size={20} />
            </QuickActionIcon>
            <QuickActionTitle>Additional Products</QuickActionTitle>
          </QuickActionHeader>
          <QuickActionDescription>
            Create curriculum packages, student sponsorships, donations, and other products.
          </QuickActionDescription>
          <QuickActionButton to="/admin/additional-products">
            Manage Products
            <FiArrowRight size={14} />
          </QuickActionButton>
        </QuickActionCard>
      </QuickActionsGrid>


      <RecentActivitySection>
        <SectionTitle>Recent Activity</SectionTitle>
        <ActivityList>
          {recentActivity.map((activity, index) => (
            <ActivityItem
              key={activity.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.7 + index * 0.1 }}
            >
              <ActivityIcon>
                {getActivityIcon(activity.type)}
              </ActivityIcon>
              <ActivityContent>
                <ActivityTitle>{activity.title}</ActivityTitle>
                <ActivityTime>{activity.time}</ActivityTime>
              </ActivityContent>
            </ActivityItem>
          ))}
        </ActivityList>
      </RecentActivitySection>
    </AdminDashboardContainer>
  );
}

export default AdminDashboard;
